import React from "react";
import PropTypes from "prop-types";
import { Table, Button, ModalFooter } from "reactstrap";
import { formatCurrency } from "common/number-formatter";

const propTypes = {
  cancelFlowData: PropTypes.shape({
    zip_spots_info: PropTypes.arrayOf(
      PropTypes.shape({
        postal_code: PropTypes.string.isRequired,
        spots: PropTypes.number.isRequired,
        price_total: PropTypes.number.isRequired,
        new_price_total: PropTypes.number.isRequired,
        spot_type: PropTypes.string.isRequired
      })
    ).isRequired,
    price_increase: PropTypes.number.isRequired
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired
};

const PriceIncreasedStep = ({ cancelFlowData, onContinue, onKeep }) => {
  const zips = Object.values(cancelFlowData?.zip_spots_info || {})
    .filter(z => z["price_increased?"])
    .map(z => ({
      postal_code: z.postal_code,
      spots: z.spots,
      spot_type: z.spot_type,
      price_total: z.price_total / 100,
      new_price_total: z.new_price_total / 100
    }));

  const totalPrice = zips.reduce((sum, z) => sum + z.price_total, 0);
  const totalNewPrice = zips.reduce((sum, z) => sum + z.new_price_total, 0);
  const savingsPct =
    totalNewPrice > 0 ? Math.round(((totalNewPrice - totalPrice) / totalNewPrice) * 100) : 0;

  return (
    <div>
      <p className="text-center font-weight-bold mb-1">
        Before you go, did you know prices have increased for other customers?
      </p>
      <p className="text-center text-muted mb-3">
        Good news — you&apos;re currently locked in at a lower rate forever.
      </p>

      <Table bordered hover size="sm" className="mb-2">
        <thead>
          <tr>
            <th>Zip Code</th>
            <th className="text-center"># of Spots</th>
            <th className="text-center">Your Rate</th>
            <th className="text-center">Other Customers</th>
          </tr>
        </thead>
        <tbody>
          {zips.map(z => (
            <tr key={`${z.postal_code}-${z.spot_type}`}>
              <td>{z.postal_code}</td>
              <td className="text-center">
                {z.spots}{" "}
                {z.spot_type
                  ? `${z.spot_type} spot${z.spots !== 1 ? "s" : ""}`
                  : `spot${z.spots !== 1 ? "s" : ""}`}
              </td>
              <td className="text-center text-success">{formatCurrency(z.price_total)}</td>
              <td className="text-center">{formatCurrency(z.new_price_total)}</td>
            </tr>
          ))}
          <tr className="font-weight-bold">
            <td colSpan={2} className="text-right">
              Total
            </td>
            <td className="text-center text-success">{formatCurrency(totalPrice)}</td>
            <td className="text-center">{formatCurrency(totalNewPrice)}</td>
          </tr>
        </tbody>
      </Table>

      {savingsPct > 0 && (
        <p className="text-center text-success mb-2">
          You&apos;re saving <strong>{savingsPct}%</strong> over other customers.
        </p>
      )}

      <p className="text-center text-muted mb-0">
        If you cancel, you&apos;ll lose your lower rate and have to pay{" "}
        <strong>{formatCurrency(totalNewPrice - totalPrice)} more</strong> to restart.
      </p>

      <ModalFooter className="pt-2 px-0">
        <Button color="secondary" className="btn-sharp" onClick={onContinue}>
          Cancel and Give Up Lower Rate
        </Button>
        <Button color="primary" className="btn-sharp" onClick={onKeep}>
          Keep My Lower Rate
        </Button>
      </ModalFooter>
    </div>
  );
};

PriceIncreasedStep.propTypes = propTypes;

export default PriceIncreasedStep;
